import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { TaskChecklistItemDto, TaskDetail } from '@fos/shared/contracts/work';
import { GripVertical, Plus, X } from 'lucide-react';
import { type CSSProperties, type FormEvent, useEffect, useRef, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Input } from '../../../ui/Input';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';

/** Czas na „Cofnij” po usunięciu pozycji (usunięcie trafia na serwer dopiero po nim). */
export const CHECKLIST_UNDO_MS = 5000;

/**
 * Checklista w karcie zadania (Figma 3020:2515): odhaczenie, dodanie, zmiana treści (kliknięcie w tekst),
 * usunięcie z „Cofnij” i kolejność (uchwyt: przeciągnij albo spacja + strzałki). Bez edycji: sam podgląd.
 * Ekran dotykowy (bez hovera): „Usuń” widoczny na stałe, uchwyt kolejności ukryty, pole odhaczenia z większym celem
 * dotyku (przegląd Fali 5: niewidoczny przycisk usuwał pozycję jednym dotknięciem).
 */
export function TaskChecklist({ task, editable }: { task: TaskDetail; editable: boolean }) {
  const utils = trpc.useUtils();
  // usunięcia czekające na „Cofnij” (id → licznik): serwer jeszcze ma te pozycje, lista ich nie pokazuje
  const pendingRemovals = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const visible = (list: TaskChecklistItemDto[]) => list.filter((i) => !pendingRemovals.current.has(i.id));
  const [items, setItems] = useState<TaskChecklistItemDto[]>(task.checklistItems);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `visible` czyta tylko ref
  useEffect(() => setItems(visible(task.checklistItems)), [task.checklistItems]);

  const refresh = () => utils.tasks.invalidate();
  const onError = (err: unknown) => {
    toast.error(errorMessage(err, 'Nie udało się zapisać checklisty.'));
    // stan z serwera wraca od razu (ta sama tablica z cache nie uruchomi efektu wyżej)
    setItems(visible(task.checklistItems));
    void refresh();
  };
  const add = trpc.tasks.checklist.add.useMutation({ onSuccess: refresh, onError });
  const toggle = trpc.tasks.checklist.toggle.useMutation({ onSuccess: refresh, onError });
  const rename = trpc.tasks.checklist.update.useMutation({ onSuccess: refresh, onError });
  const reorder = trpc.tasks.checklist.reorder.useMutation({ onSuccess: refresh, onError });
  const remove = trpc.tasks.checklist.remove.useMutation({ onSuccess: refresh, onError });

  // wyjście z karty przed upływem „Cofnij”: usunięcie i tak trafia na serwer
  useEffect(() => {
    const pending = pendingRemovals.current;
    return () => {
      for (const [id, timer] of pending) {
        clearTimeout(timer);
        void utils.client.tasks.checklist.remove.mutate({ id }).catch(() => undefined);
      }
      pending.clear();
    };
  }, [utils]);

  const removeWithUndo = (item: TaskChecklistItemDto) => {
    const index = items.findIndex((i) => i.id === item.id);
    const timer = setTimeout(() => {
      pendingRemovals.current.delete(item.id);
      remove.mutate({ id: item.id });
    }, CHECKLIST_UNDO_MS);
    pendingRemovals.current.set(item.id, timer);
    setItems((list) => list.filter((i) => i.id !== item.id));
    toast('Usunięto pozycję', {
      description: item.text,
      duration: CHECKLIST_UNDO_MS,
      action: {
        label: 'Cofnij',
        onClick: () => {
          const pending = pendingRemovals.current.get(item.id);
          if (!pending) return;
          clearTimeout(pending);
          pendingRemovals.current.delete(item.id);
          setItems((list) => {
            if (list.some((i) => i.id === item.id)) return list;
            const next = [...list];
            next.splice(Math.min(Math.max(index, 0), next.length), 0, item);
            return next;
          });
        },
      },
    });
  };

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(items, from, to);
    setItems(next);
    const index = next.findIndex((i) => i.id === active.id);
    reorder.mutate({ id: String(active.id), afterId: index > 0 ? (next[index - 1]?.id ?? null) : null });
  };

  const submitNew = (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) {
      setAdding(false);
      return;
    }
    add.mutate({ taskId: task.id, text }, { onSuccess: () => setDraft('') });
  };

  if (items.length === 0 && !editable) return null;

  return (
    <section aria-label="Checklista">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
        accessibility={{
          screenReaderInstructions: {
            draggable: 'Aby zmienić kolejność, naciśnij spację, przesuń strzałkami i naciśnij spację. Escape anuluje.',
          },
          announcements: {
            onDragStart: () => 'Podniesiono pozycję checklisty.',
            onDragOver: () => undefined,
            onDragEnd: () => 'Upuszczono pozycję checklisty.',
            onDragCancel: () => 'Anulowano zmianę kolejności.',
          },
        }}
      >
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <ul>
            {items.map((item) => (
              <ChecklistRow
                key={item.id}
                item={item}
                editable={editable}
                onToggle={(done) => {
                  setItems((list) => list.map((i) => (i.id === item.id ? { ...i, done } : i)));
                  toggle.mutate({ id: item.id, done });
                }}
                onRename={(text) => rename.mutate({ id: item.id, text })}
                onRemove={() => removeWithUndo(item)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      {editable &&
        (adding ? (
          <form onSubmit={submitNew} className="mt-1 flex items-center gap-2 pl-[30px]">
            <Input
              size="s"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setAdding(false);
                  setDraft('');
                }
              }}
              placeholder="Nowa pozycja, Enter dodaje"
              maxLength={500}
              aria-label="Nowa pozycja checklisty"
            />
            <Button type="submit" size="s" variant="secondary" loading={add.isPending} className={TOUCH_BUTTON}>
              Dodaj
            </Button>
          </form>
        ) : (
          <Button
            variant="ghost"
            size="s"
            icon={Plus}
            className={cn('mt-1 -ml-2.5', TOUCH_BUTTON)}
            onClick={() => setAdding(true)}
          >
            Dodaj pozycję
          </Button>
        ))}
    </section>
  );
}

function ChecklistRow({
  item,
  editable,
  onToggle,
  onRename,
  onRemove,
}: {
  item: TaskChecklistItemDto;
  editable: boolean;
  onToggle: (done: boolean) => void;
  onRename: (text: string) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !editable,
  });
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  useEffect(() => setText(item.text), [item.text]);
  const style: CSSProperties = {
    transform: transform ? `translate3d(0, ${Math.round(transform.y)}px, 0)` : undefined,
    transition,
  };
  const save = () => {
    setEditing(false);
    const next = text.trim();
    if (next && next !== item.text) onRename(next);
    else setText(item.text);
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative -ml-6 flex min-h-[37px] items-center gap-3 rounded-md pl-6',
        isDragging && 'z-10 bg-canvas shadow-popover',
      )}
    >
      {editable && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label={`Zmień kolejność: ${item.text}`}
          className={cn(
            'absolute left-0 hidden size-6 cursor-grab items-center justify-center rounded-md text-fg-muted',
            // tylko z myszą (hover): na ekranie dotykowym niewidoczny uchwyt byłby celem bez znaczenia
            '[@media(hover:hover)]:inline-flex opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
            'focus-visible:outline-2 focus-visible:outline-accent',
          )}
        >
          <GripVertical size={14} strokeWidth={1.75} aria-hidden />
        </button>
      )}
      <Checkbox
        checked={item.done}
        onCheckedChange={onToggle}
        disabled={!editable}
        className="shrink-0"
        touchTarget
        label={<span className="sr-only">{item.text}</span>}
      />
      {editing ? (
        <Input
          size="s"
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') {
              setText(item.text);
              setEditing(false);
            }
          }}
          maxLength={500}
          aria-label="Treść pozycji"
        />
      ) : (
        <button
          type="button"
          disabled={!editable}
          onClick={() => setEditing(true)}
          className={cn(
            'min-w-0 flex-1 rounded-sm py-1.5 text-left text-body-m disabled:cursor-default',
            'focus-visible:outline-2 focus-visible:outline-accent',
            item.done ? 'text-fg-muted line-through' : 'text-fg',
          )}
        >
          {item.text}
        </button>
      )}
      {editable && !editing && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Usuń pozycję: ${item.text}`}
          className={cn(
            'inline-flex size-9 shrink-0 items-center justify-center rounded-md text-fg-muted',
            // mysz: pokazany po najechaniu na wiersz; ekran dotykowy (bez hovera): widoczny zawsze, większy cel
            '[@media(hover:hover)]:size-7 [@media(hover:hover)]:opacity-0 group-hover:opacity-100',
            'hover:bg-elevated hover:text-fg focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-accent',
          )}
        >
          <X size={14} strokeWidth={1.75} aria-hidden />
        </button>
      )}
    </li>
  );
}
